import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Award, Target } from 'lucide-react';
import { apiService } from '../services/api';
import { Loading, ErrorMessage, Card } from '../components/SharedComponents';
import { debounce } from '../utils/debounce';

const BATTING_STAT_TYPES = [
  { key: 'most_runs', label: 'Most Runs', icon: TrendingUp },
  { key: 'most_runs_innings', label: 'Highest Score', icon: Award },
  { key: 'highest_average', label: 'Highest Average', icon: Target },
  { key: 'highest_strikerate', label: 'Highest SR', icon: TrendingUp },
  { key: 'highest_strikerate_innings', label: 'Highest SR (Inn)', icon: Award },
  { key: 'most_run100', label: 'Most 100s', icon: Award },
  { key: 'most_run50', label: 'Most 50s', icon: Award },
  { key: 'most_run4', label: 'Most 4s', icon: Target },
  { key: 'most_run4_innings', label: 'Most 4s (Inn)', icon: Award },
  { key: 'most_run6', label: 'Most 6s', icon: Target },
  { key: 'most_run6_innings', label: 'Most 6s (Inn)', icon: Award }
];

function BattingStats() {
  const navigate = useNavigate();
  const [selectedStat, setSelectedStat] = useState('most_runs');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBattingStats = useCallback(async (statType) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getBattingStats(statType);

      let stats = [];
      if (response.data) {
        if (response.data.stats && Array.isArray(response.data.stats)) {
          stats = response.data.stats;
        } else if (response.data.response && response.data.response.stats) {
          stats = response.data.response.stats;
        } else if (Array.isArray(response.data)) {
          stats = response.data;
        } else if (response.data.response && Array.isArray(response.data.response)) {
          stats = response.data.response;
        }
      } else if (Array.isArray(response)) {
        stats = response;
      }

      setData(stats);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching batting stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedFetch = useCallback(
    debounce((statType) => fetchBattingStats(statType), 300),
    [fetchBattingStats]
  );

  useEffect(() => {
    debouncedFetch(selectedStat);
  }, [selectedStat, debouncedFetch]);

  const handleStatChange = (statKey) => {
    setSelectedStat(statKey);
  };

  if (loading) return <Loading message="Loading batting statistics..." />;
  if (error) return <ErrorMessage message={error} onRetry={() => fetchBattingStats(selectedStat)} />;

  const currentStat = BATTING_STAT_TYPES.find(s => s.key === selectedStat);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <h1 style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: '32px',
          fontWeight: '700',
          color: '#1c1917',
          letterSpacing: '-0.5px'
        }}>Batting Statistics</h1>
        <p style={{ color: '#57534e', marginTop: '6px', fontSize: '15px' }}>Comprehensive batting performance metrics</p>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {BATTING_STAT_TYPES.map((stat) => {
          const Icon = stat.icon;
          const isSelected = selectedStat === stat.key;
          return (
            <button
              key={stat.key}
              onClick={() => handleStatChange(stat.key)}
              style={{
                padding: '10px 16px',
                borderRadius: '10px',
                border: isSelected ? '2px solid #e85d04' : '1px solid #e7e5e4',
                backgroundColor: isSelected ? 'rgba(232, 93, 4, 0.08)' : 'white',
                color: isSelected ? '#e85d04' : '#1c1917',
                fontWeight: isSelected ? '600' : '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                transition: 'all 0.2s ease',
                boxShadow: isSelected ? '0 2px 8px rgba(232, 93, 4, 0.15)' : 'none'
              }}
            >
              <Icon style={{ width: '14px', height: '14px' }} />
              {stat.label}
            </button>
          );
        })}
      </div>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          {currentStat && (
            <>
              <div style={{
                padding: '10px',
                background: 'linear-gradient(135deg, #e85d04 0%, #ff7b29 100%)',
                borderRadius: '10px'
              }}>
                <currentStat.icon style={{ width: '20px', height: '20px', color: 'white' }} />
              </div>
              <h2 style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: '20px',
                fontWeight: '600',
                color: '#1c1917'
              }}>
                {currentStat.label}
              </h2>
            </>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>#</th>
                <th style={{ ...tableHeaderStyle, textAlign: 'left' }}>Player</th>
                <th style={{ ...tableHeaderStyle, textAlign: 'left' }}>Team</th>
                <th style={tableHeaderStyle}>M</th>
                <th style={tableHeaderStyle}>Runs</th>
                {selectedStat.includes('average') && <th style={tableHeaderStyle}>Avg</th>}
                {selectedStat.includes('strikerate') && <th style={tableHeaderStyle}>SR</th>}
                {selectedStat.includes('run100') && <th style={tableHeaderStyle}>100s</th>}
                {selectedStat.includes('run50') && <th style={tableHeaderStyle}>50s</th>}
                {selectedStat.includes('run4') && <th style={tableHeaderStyle}>4s</th>}
                {selectedStat.includes('run6') && <th style={tableHeaderStyle}>6s</th>}
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 50).map((player, index) => {
                const playerName = player.player?.title || player.player?.name || player.name || 'N/A';
                const playerId = player.player?.pid || player.pid || player.player_id;
                const teamName = player.team?.title || player.team?.name || player.team_name || 'N/A';
                const runs = player.runs || player.highest || 0;
                const matches = player.matches || player.innings || 0;

                return (
                  <tr
                    key={index}
                    style={{ transition: 'background-color 0.15s' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fafaf9'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ ...tableCellStyle, color: '#57534e', width: '50px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        backgroundColor: index < 3 ? 'rgba(232, 93, 4, 0.1)' : '#f5f5f4',
                        color: index < 3 ? '#e85d04' : '#57534e',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600',
                        fontSize: '12px',
                        margin: '0 auto'
                      }}>
                        {index + 1}
                      </div>
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'left' }}>
                      {playerId ? (
                        <span
                          onClick={() => navigate(`/players/${playerId}`)}
                          style={{
                            color: '#e85d04',
                            cursor: 'pointer',
                            fontWeight: '500',
                            textDecoration: 'none'
                          }}
                          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                          {playerName}
                        </span>
                      ) : (
                        <span style={{ fontWeight: '500' }}>{playerName}</span>
                      )}
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'left', color: '#57534e', fontSize: '13px' }}>
                      {teamName}
                    </td>
                    <td style={tableCellStyle}>{matches || '-'}</td>
                    <td style={{ ...tableCellStyle, fontWeight: '700', color: '#1c1917' }}>{runs || '-'}</td>
                    {selectedStat.includes('average') && (
                      <td style={tableCellStyle}>{player.average || '-'}</td>
                    )}
                    {selectedStat.includes('strikerate') && (
                      <td style={tableCellStyle}>{player.strike || player.strike_rate || '-'}</td>
                    )}
                    {selectedStat.includes('run100') && (
                      <td style={{ ...tableCellStyle, color: '#e85d04', fontWeight: '600' }}>{player.run100 || '-'}</td>
                    )}
                    {selectedStat.includes('run50') && (
                      <td style={{ ...tableCellStyle, color: '#e85d04', fontWeight: '600' }}>{player.run50 || '-'}</td>
                    )}
                    {selectedStat.includes('run4') && (
                      <td style={tableCellStyle}>{player.run4 || '-'}</td>
                    )}
                    {selectedStat.includes('run6') && (
                      <td style={tableCellStyle}>{player.run6 || '-'}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

const tableHeaderStyle = {
  padding: '12px 14px',
  textAlign: 'center',
  fontWeight: '600',
  fontSize: '12px',
  color: '#57534e',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderBottom: '1px solid #e7e5e4',
  backgroundColor: '#fafaf9'
};

const tableCellStyle = {
  padding: '14px',
  textAlign: 'center',
  fontSize: '14px',
  color: '#1c1917',
  borderBottom: '1px solid #f5f5f4'
};

export default BattingStats;
//EOF